import * as Constants from '@/redux/constants';
import { Divider, Stack, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import paymentIcons from 'assets/payment-icon.png';
import qfpayIcon from 'assets/qfpay-icon.png';
import { GeneralInput } from 'components/input';
import { LanguageSwitcher } from 'components/language-switcher';
import { useI18n } from 'locales';
import get from 'lodash/get';
import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import FundSelectBox from './FundSelectBox';
import GiveButton from './GiveButton';
import Heading from './Heading';
import Subheading from './Subheading';
import TypeSelectBox from './TypeSelectBox';

function EntryFormPage({
  itemInfo,
  optionsItemInfo,
  control,
  errors,
  fields,
  registerOptions,
  isFetching,
  onSubmit,
  watch,
  setValue,
}) {
  // const watchedRequireReceipt = watch('requireReceipt');
  const watchedFundId = watch('fundId');
  const watchedType = watch('type');
  const watchedAmount = watch('amount');
  const t = useI18n();

  // Auto-select payment method if there's only one option
  useEffect(() => {
    const paymentMethods = get(optionsItemInfo, 'data.paymentMethodId', []);
    if (paymentMethods.length === 1 && !watch('paymentMethodId')) {
      setValue('paymentMethodId', paymentMethods[0].value);
    }
  }, [optionsItemInfo, setValue, watch]);

  useEffect(() => {
    switch (itemInfo.type) {
      case Constants.CREATE_PUBLIC_GIVING.SUCCESS: {
        const paymentUrl = get(itemInfo, 'data.onlinePayment.paymentUrl');
        if (paymentUrl) {
          location.href = paymentUrl;
        }
      }
        break;
    }
  }, [itemInfo]);

  const fieldMap = fields.reduce((acc, field) => {
    field = field[0]; // Unwrap field array

    if (field.name === 'fundId' || field.name === 'type') {
      return acc;
    }

    acc[field.name] = (
      <GeneralInput
        key={`entry-form-input-${field.name}`}
        {...field}
        label={t(field.label)}
        placeholder={field.placeholder ? t(field.placeholder) : undefined}
        control={control}
        registerOptions={registerOptions[field.name]}
        options={get(optionsItemInfo, `data.${field.name}`)}
        readOnly={isFetching}
        error={get(errors, field.name)}
        helper={get(errors, `${field.name}.message`) ?? field.helper}
      />
    );
    return acc;
  }, {});

  return (
    <Stack sx={{ position: 'relative' }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ py: 2, px: 2 }}>
        <LanguageSwitcher />
      </Stack>

      <Heading />

      <form onSubmit={onSubmit} className="w-full flex flex-col items-center">
        <Grid container gap={2} direction="row" sx={{ alignItems: 'center', py: 3, width: '100%' }}>
          <Controller
            name="fundId"
            control={control}
            rules={registerOptions.fundId}
            render={({ field: { onChange } }) => {
              const error = get(errors, 'fundId');
              return (
                <FormControl
                  variant='filled'
                  error={!!error}
                  fullWidth
                >
                  <Grid gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', width: '100%', maxWidth: 1200, px: 4, pb: 1, marginX: 'auto' }}>
                    {
                      optionsItemInfo.data?.fundId?.map((fund, index) => (
                        <FundSelectBox
                          key={`fund-select-box-${index}`}
                          fund={fund}
                          selected={fund.value === watchedFundId}
                          onSelect={() => onChange(fund.value)}
                        />
                      ))
                    }
                  </Grid>
                  <FormHelperText sx={{ textAlign: 'center' }}>{t(get(error, 'message'))}</FormHelperText>
                </FormControl>
              );
            }}
          />
        </Grid>

        <Grid container direction="column" sx={{ alignItems: 'center', py: 3, width: '100%', display: 'none' }}>
          <Subheading>FREQUENCY</Subheading>
          <Controller
            name="type"
            control={control}
            rules={registerOptions.type}
            render={({ field: { onChange } }) => {
              const error = get(errors, 'type');
              return (
                <FormControl
                  variant='filled'
                  error={!!error}
                  fullWidth
                >
                  <Stack gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, display: 'flex', justifyContent: 'center', width: { xs: '100%', sm: '500px' }, px: 4, pb: 1, marginX: 'auto' }}>
                    {
                      optionsItemInfo.data?.type?.map((type, index) => (
                        <TypeSelectBox
                          key={`type-select-box-${index}`}
                          type={type}
                          selected={type.value === watchedType}
                          onSelect={() => onChange(type.value)}
                        />
                      ))
                    }
                  </Stack>
                  <FormHelperText>{t(get(error, 'message'))}</FormHelperText>
                </FormControl>
              );
            }}
          />
        </Grid>

        <Grid sx={{
          width: '100%',
          maxWidth: '500px',
          p: 4,
        }}>
          <Subheading>{t('AMOUNT')}</Subheading>

          <Stack gap={1}>
            {fieldMap.amount}
            {fieldMap.paymentMethodId}
          </Stack>

          <Divider sx={{ mb: 5, borderColor: 'transparent' }} />

          <Subheading>{t('PAYMENT CONFIRMATION')}</Subheading>

          <Stack gap={1}>
            {/* {fieldMap.giverName} */}
            {fieldMap.giverEmail}

            <Typography variant='caption' color='text.secondary'>
              {t('Please Note: By choosing \'Do Not Require A Receipt\', no receipt shall be issued or requested in the future regards to the giving, yet email is compulsory for each giving for issuing confirmation email and documentation purpose.')}
            </Typography>
          </Stack>

          <Stack gap={1} sx={{ mb: 4 }}>
            <GiveButton
              amount={watchedAmount}
              isFetching={isFetching}
            />
          </Stack>

          <Grid container direction="column" gap={1.5} sx={{ alignItems: 'center', py: 1.5 }}>
            <Typography variant='caption' align='center' color='info.700'>
              {t('Click this button to proceed to the QFPay payment page')}
            </Typography>

            <img src={qfpayIcon} alt='qfpayIcon' />

            <img src={paymentIcons} alt='paymentIcons' />
          </Grid>
        </Grid>
      </form>
    </Stack>
  );
}

export default EntryFormPage;
